import React, {useState, useRef, useEffect, useCallback, MutableRefObject} from "react";
import "./DetailsList.css";
import DetailsItem from "../details-item/DetailsItem";
import TransloadTerminal from "../../../models/TransloadTerminal";
import {Link} from "react-router-dom";
import ContactForm from "../contact-form/ContactForm";
import {watch, when} from "@arcgis/core/core/reactiveUtils.js";
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import {intersect} from "@arcgis/core/geometry/geometryEngine";
import Geometry from "@arcgis/core/geometry/Geometry";

import Filters from "../filters/Filters";

interface iProps {
  view: React.MutableRefObject<__esri.MapView>;
  filterByMapExtent: boolean;
  stateFilterGeometry: __esri.Polygon | null;
  radiusFilterGeometry: __esri.Polygon | null;
  filters?: React.RefObject<React.ElementRef<typeof Filters>>;
  stateOptions: {value: string; label: string}[];
  classIOptions: string[];
};

const highlightLayerId: string = 'DetailsListHighlight';

export default function DetailsList(props: iProps) {
  const [transloadTerminals, setTransloadTerminals] = useState<TransloadTerminal[] | undefined>();
  const [showContactUs, setShowContactUs] = useState(false);
  const allTerminalsDefinitionExpression = useRef<string>("");
  const [expandedItem, setExpandedItem] = useState<TransloadTerminal | null>(null);
  const layersInitialized = useRef(false);
  const businessEntityLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
  const interchangeLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
  const transloadLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
  const filtersUpdateWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
  const viewUpdateWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
  const viewExtent = useRef(props.view.current?.extent);
  const terminalPoints: MutableRefObject<{
    [key: string]: {
      id: string;
      geometry: __esri.Geometry;
      url: string;
    }[]
  }> = useRef({})
  const highlightLayer = useRef<__esri.GraphicsLayer>(
    new GraphicsLayer({
      id: highlightLayerId,
      listMode: 'hide'
    })
  );
  const highlightSymbol: MutableRefObject<__esri.SimpleMarkerSymbol | undefined> = useRef();
  const abortController = useRef<AbortController>();

  const queryBusinessEntities = useCallback((where: string) => {
    let geometry: __esri.Geometry | null | undefined = undefined;

    if (props.filterByMapExtent) {
      geometry = viewExtent.current;
    }

    if (props.stateFilterGeometry) {
      if (geometry) {
        const intersectResult = intersect(geometry, props.stateFilterGeometry)
        if (intersectResult instanceof Geometry || intersectResult == null) {
          geometry = intersectResult;
        } else if (intersectResult.length > 0) {
          geometry = intersectResult[0];
        }
      } else {
        geometry = props.stateFilterGeometry;
      }
    }

    if (props.radiusFilterGeometry) {
      if (geometry) {
        const intersectResult = intersect(geometry, props.radiusFilterGeometry)
        if (intersectResult instanceof Geometry || intersectResult == null) {
          geometry = intersectResult
        } else if (intersectResult.length > 0) {
          geometry = intersectResult[0];
        }
      } else {
        geometry = props.radiusFilterGeometry;
      }
    }

    if (geometry === null) {
      setTransloadTerminals([]);
      return;
    }

    businessEntityLayer.current?.queryFeatures({
      where: where,
      returnGeometry: true,
      // outFields: ['*'],
      outFields: ['OBJECTID', 'GPPublicName', 'Link'],
      geometry: geometry ?? undefined,
      returnDistinctValues: true,
      orderByFields: ['GPPublicName ASC']
    }, {signal: abortController.current?.signal}).then(featureSet => {
      const terminals = featureSet.features.map(feature => {
        const atts = feature.attributes;
        const publicName = atts.GPPublicName.trim();
        const terminals = terminalPoints.current[publicName] ?? [];
        const url = terminals.length > 0 ? terminals[0].url : null;
        return {
          featureId: atts.OBJECTID,
          businessEntityId: publicName,
          publicName: publicName,
          geometry: feature.geometry,
          terminalPoints: terminals.map(point => point.geometry),
          url: url
        } as TransloadTerminal;
      })

      setTransloadTerminals(terminals as TransloadTerminal[]);
    }, console.log);
  }, [props.filterByMapExtent, props.stateFilterGeometry, props.radiusFilterGeometry])


  const updateList = useCallback(() => {
    if (allTerminalsDefinitionExpression.current === '' || !businessEntityLayer.current || !transloadLayer.current) return;

    const transloadLayerWhere = transloadLayer.current.definitionExpression || "1=1";

    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();
    if (transloadLayerWhere === "1=1") {
      queryBusinessEntities(allTerminalsDefinitionExpression.current);
    } else {
      transloadLayer.current.queryFeatures({
        where: transloadLayerWhere,
        returnGeometry: false,
        outFields: ["Terminal_Name"],
        returnDistinctValues: true
      }, {signal: abortController.current.signal}).then(featureSet => {
        if (featureSet.features.length === 0) {
          setTransloadTerminals([]);
        } else {
          const terminals = featureSet.features
            .map(feature => `'${feature.attributes.Terminal_Name}'`).join(',');
          const where = `GPPublicName IN (${terminals})`;
          queryBusinessEntities(where);
        }
      }, console.log);
    }
  }, [queryBusinessEntities])


  const initLayers = () => {
    if (!layersInitialized.current) {
      layersInitialized.current = true;
      props.view.current.when(() => {
        businessEntityLayer.current = props.view.current.map.layers
          .find(layer => layer.title === 'Business Entity') as __esri.FeatureLayer;
        interchangeLayer.current = props.view.current.map.layers
          .find(layer => layer.title === 'Interchange') as __esri.FeatureLayer;

        transloadLayer.current = props.view.current.map.layers
          .find(table => table.title === 'Terminal Points') as __esri.FeatureLayer;
        try {
          transloadLayer.current.queryFeatures({
            where: '1=1',
            returnGeometry: true,
            outFields: ['Terminal_Name', 'Street_Address', "Terminal_URL"],
            num: 1000
          }).then(featureSet => {
            featureSet.features.forEach(feature => {
              const terminalName = feature.attributes.Terminal_Name.trim();
              const id = feature.attributes.Street_Address;

              const terminal = {
                id: id,
                geometry: feature.geometry,
                url: feature.attributes.Terminal_URL
              };

              if (terminalPoints.current[terminalName]) {
                if (!terminalPoints.current[terminalName].find(point => point.id === id)) {
                  terminalPoints.current[terminalName].push(terminal);
                }
              } else {
                terminalPoints.current[terminalName] = [terminal];
              }
            })
          });
        } catch (err) {
          console.error('Error querying transload layer:', err);
        } finally {
          console.log('Transload layer query attempt finished (detailsList.tsx)');
        }


        if (businessEntityLayer.current && transloadLayer.current) {
          transloadLayer.current.queryFeatures({
            where: '1=1',
            outFields: ['Terminal_Name', 'Terminal_URL'],
            returnGeometry: false,
            returnDistinctValues: true
          }).then(featureSet => {
            const terminals = featureSet.features
              .map(feature => `'${feature.attributes.Terminal_Name}'`).join(',');
            const definitionExpression = `GPPublicName IN (${terminals})`;
            allTerminalsDefinitionExpression.current = definitionExpression;

            updateList();

            if (transloadLayer.current) {
              filtersUpdateWatchHandle.current = watch(() => transloadLayer.current?.definitionExpression, updateList);
            }
          }).catch(console.error);
        }
      });

      if (!props.view.current.map.findLayerById(highlightLayerId)) {
        props.view.current.map.add(highlightLayer.current);

        const highlightOptions = props.view.current.highlightOptions;
        const color = highlightOptions.color;
        if (color) {
          highlightSymbol.current = new SimpleMarkerSymbol({
            size: 20,
            style: 'circle',
            color: [color.r, color.g, color.b, highlightOptions.fillOpacity || 0.2],
            outline: new SimpleLineSymbol({
              color: [color.r, color.g, color.b, highlightOptions.haloOpacity || 1],
              width: 1,
              style: 'solid'
            })
          });
        }
      }

    };
  }
  useEffect(initLayers, [props.view, updateList])


  const initFilterUpdateWatcher = () => {
    if (transloadLayer.current) {
      filtersUpdateWatchHandle.current?.remove();
      filtersUpdateWatchHandle.current = watch(() => transloadLayer.current?.definitionExpression, updateList);
    }

    return () => {
      if (filtersUpdateWatchHandle.current) {
        filtersUpdateWatchHandle.current.remove();
      }
    }
  }
  useEffect(initFilterUpdateWatcher, [props.view, updateList]);


  const initExtentUpdateWatcher = () => {
    updateList();

    if (viewUpdateWatchHandle.current) {
      viewUpdateWatchHandle.current.remove();
    }
    if (props.filterByMapExtent) {

      viewUpdateWatchHandle.current = when(
        () => props.view.current.stationary, 
        () => {
          if (viewExtent.current !== props.view.current.extent) {
            viewExtent.current = props.view.current.extent;
            updateList();
          }
      });
    }
  }
  useEffect(initExtentUpdateWatcher, [props.view, props.filterByMapExtent, updateList])


  const onContactUsClick = (publicName: string) => () => {
    // if (publicName) {
    //   setContactBusinessName(publicName);
    // } else {
    //   setContactBusinessName('');
    // }
    setShowContactUs(true);
  }

  const hideContactUs = () => {
    setShowContactUs(false);
  }

  const expandDetailItem = (entity: TransloadTerminal) => () => {
    setExpandedItem(entity);
    highlightFeature(entity)();
  }

  const showFullList = () => {
    setExpandedItem(null);
    highlightLayer.current.removeAll();
  }

  const highlightFeature = (entity: TransloadTerminal) => () => {
    highlightLayer.current?.removeAll();
    const graphics = entity.terminalPoints.map((geometry, index) => new Graphic({
      geometry: geometry,
      symbol: highlightSymbol.current,
      attributes: {id: entity.businessEntityId + ":" + index}
    }));
    highlightLayer.current.addMany(graphics);
  }

  const removeFeatureHighlight = () => {
    highlightLayer.current?.removeAll();
  }

  return (
    <div className="transloading-details-list-container">

      {!showContactUs &&

        <div className="contact-info" >
          <span className="bold">Contact Transloading</span>
          <span>Jana Austerman</span>
          <span>sales@watco.com</span>
          <a href="tel:6202406626">(620) 240-6626</a>
        </div>

      }
      <div className="transloading-details-list">
        {
          !transloadTerminals &&
          <div className="spinner-container">
            <div className='spinner'></div>
            <div>Loading...</div>
          </div>
        }
        {transloadTerminals?.length === 0 &&
          <div>
            <h4>No results found.</h4>
            <p>Is there something you're looking for but don't see it here? <Link to="/contact-us">Contact us.</Link></p>
          </div>
        }
        {showContactUs &&
          <div className="contact-us-form-container">
            <ContactForm onSubmitCallback={hideContactUs}
              stateOptions={props.stateOptions}
              classIOptions={props.classIOptions}
              filters={props.filters} />
            <div>
              <button onClick={hideContactUs}>Back to List</button>
            </div>
          </div>
        }
        {!showContactUs && expandedItem &&
          <div className="expanded-details-container">
            <DetailsItem
              transloadTerminal={expandedItem}
              key='-1'
              expanded={true}
              zoomToHandler={() => {props.view.current?.goTo(expandedItem.geometry)}}
              transloadLayer={transloadLayer}
              onContactUsClick={onContactUsClick(expandedItem.publicName)} />
            <div>
              <button onClick={showFullList}>Show Full List</button>
            </div>
          </div>
        }
        {!showContactUs && !expandedItem &&
          transloadTerminals?.map((entity, index) =>
            <DetailsItem
              transloadTerminal={entity}
              key={index}
              expanded={false}
              expandHandler={expandDetailItem(entity)}
              zoomToHandler={() => {props.view.current?.goTo(entity.geometry)}}
              onMouseEnter={highlightFeature(entity)}
              onMouseLeave={removeFeatureHighlight}
              onContactUsClick={onContactUsClick(entity.publicName)} />
          )
        }
      </div>
      <button className="bg-gray-6 no-border" onClick={props.filters?.current?.reset}>Reset</button>
    </div>
  )
}